import React, { useState } from 'react';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useSelector, useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { Arrow, PlusPrimary } from '@/icons/index';
import { statusInfoMap } from '@/constants/cabinet';
import {
  selectors as cabinetSelectors,
  actions as cabinetActions,
  hooks as cabinetHooks,
} from '@/domains/cabinet';
import { langFormatter } from '@/utils/languageFormat';
import { useCabinetMenu } from '@/hooks/useCabinetMenu';
import { NewDropdownCard } from '@/icons/NewDropdownCard';
import { formatPhoneNumberNew } from '@/utils/index';
import generateInitialToken from '@/utils/generateInitialToken';
import { actions } from '@/domains/cabinet/slice';

import styles from './Dropdown.module.scss';

const Dropdown = ({ label, className }) => {
  const { t, lang } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();
  const formattedLang = langFormatter({ lang });
  const { data: accounts } = cabinetHooks.useGetAccountsQuery({
    lang: formattedLang,
  });
  const [subscribeTrigger] = cabinetHooks.useSecondarySubscriberCheck();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const signInHandler = async (data) => {
    const phoneNumber = `994${data?.phone?.number}`;
    const initialToken = generateInitialToken(phoneNumber, uuidv4());
    dispatch(actions.setInitialToken(initialToken));
    sessionStorage.setItem('initialToken', initialToken);
    await subscribeTrigger({
      msisdn: phoneNumber,
      lang: formattedLang,
      initialToken,
    }).unwrap();
    setActiveAddAccount(true);
    dispatch(actions.setAdditionalNumber(data?.phone?.number));
    dispatch(actions.setSignInModal(true));
  };

  const { setActiveAddAccount } = useCabinetMenu();

  const selectOption = (id) => {
    if (
      typeof window !== 'undefined' &&
      sessionStorage.getItem('refreshToken') &&
      sessionStorage.getItem('accountId') &&
      accounts?.find((el) => el?.id === id).numberStatus === 'ACTIVE'
    ) {
      sessionStorage.setItem('accountId', id);
      dispatch(cabinetActions.setSelectedAccountId(id));
      setIsOpen(false);
    }
    if (
      typeof window !== 'undefined' &&
      sessionStorage.getItem('refreshToken') &&
      sessionStorage.getItem('accountId') &&
      accounts?.find((el) => el?.id === id).numberStatus === 'DEACTIVE'
    ) {
      signInHandler(accounts?.find((el) => el?.id === id));
    }
  };
  const openAddAcount = () => {
    setIsOpen(false);
    setActiveAddAccount(true);
  };
  const { selectedAccountId } = useSelector(cabinetSelectors.selectCabinet);

  return (
    <div
      className={classNames([styles.dropdown_container, className])}
      itemScope
      itemType='https://schema.org/Thing'
    >
      <button
        aria-label={label}
        className={styles.toggle}
        onClick={toggleDropdown}
      >
        {`${label} ${accounts?.length}`}
        <Arrow width={16} />
      </button>
      {isOpen && (
        <ul className={styles.list}>
          {accounts?.map((option) => (
            <li
              className={classNames({
                [styles.disabled]: option?.numberStatus === 'DEACTIVE',
              })}
              key={option?.id}
              onClick={() => selectOption(option?.id)}
            >
              <div className={styles.detail_box}>
                <span
                  className={
                    statusInfoMap[
                      option?.numberStatus !== 'DEACTIVE'
                        ? option?.status
                        : option?.numberStatus
                    ].class
                  }
                >
                  {<NewDropdownCard />}
                </span>
                <div className={styles.number_detail}>
                  <h5
                    className={classNames(styles.number, {
                      disabled: option?.numberStatus === 'DEACTIVE',
                    })}
                  >
                    {formatPhoneNumberNew(option?.phone?.number)}

                    {option?.isPrimary && (
                      <span className={styles.label}>{t('cabinet:main')}</span>
                    )}
                  </h5>
                  <p
                    className={classNames([
                      styles.status,
                      statusInfoMap[
                        option?.numberStatus === 'ACTIVE'
                          ? option?.status
                          : option?.numberStatus
                      ].class,
                    ])}
                  >
                    {t(
                      `cabinet:${
                        statusInfoMap[
                          option?.numberStatus === 'ACTIVE'
                            ? option?.status
                            : option?.numberStatus
                        ].text
                      }`,
                    )}
                  </p>
                  {option?.status === 'ACTIVE' &&
                    option?.numberStatus === 'ACTIVE' && (
                      <p className={styles.balance}>
                        • <span>{option?.balance} </span>
                        azn
                      </p>
                    )}
                </div>
              </div>
              {option?.id === Number(selectedAccountId) && (
                <Image
                  src='/assets/checkMark.svg'
                  width={15}
                  height={10}
                  alt='checkmark icon'
                  title='checkmark icon'
                />
              )}
            </li>
          ))}
          <li onClick={openAddAcount}>
            <div className={styles.detail_box}>
              <PlusPrimary className={styles.add_icon} />
              <p className={styles.add_new}>
                {t('cabinet:accounts.AddNewNumber')}
              </p>
            </div>
          </li>
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
